import { Input, InputNumber, Select, DatePicker, Space } from "antd";
import dayjs from "dayjs";
import { OperatorEnum } from "@src/pages/intelligence-search/enum";
import { config } from "@src/config";
import { useApiUrl, useCustom } from "@refinedev/core";
import { SmlAsset, QueryResultType, SmlJsonQuery } from "@src/types/intelligence-search";
import { createRendererMapping } from "./asset-renderer";

type DataType = "string" | "int" | "float" | "date" | "bool";

export type TSearchFilterFormValueProps = {
    productCode?: string;
    platform?: string[];
    timeRange?: dayjs.Dayjs[];
};

type Props = TSearchFilterFormValueProps & {
    operator: OperatorEnum;
    dataType: DataType;
    value: any;
    onChange: (val: any) => void;
    assetMeta: SmlAsset;
};

type FilterStringValueInputProps = {
    value: any;
    mode?: "single" | "multiple";
    commonProps?: object;
    onChange: (val: any) => void;
    assetMeta: SmlAsset;
} & TSearchFilterFormValueProps;

const FilterEventNameInput = ({
    value,
    mode = "single",
    commonProps,
    onChange,
    productCode,
}: FilterStringValueInputProps) => {
    /*
  Because of performance concerns, We could not store json query and load it every time a user hit filter modal.
  So that we hardcoded select options by user's registered tracking plan.
  
  */
    const apiUrl = useApiUrl("nestjsx");

    const { data, isLoading } = useCustom<{ event_name: string }[]>({
        method: "get",
        dataProviderName: "nestjsx",
        url: `${apiUrl}/products/event-name/${productCode}`,
        queryOptions: {
            enabled: Boolean(productCode),
        },
        config: {
            headers: {
                "Product-Code": productCode,
            },
        },
    });

    return (
        <Select
            {...commonProps}
            mode="tags"
            value={value}
            onChange={onChange}
            loading={isLoading}
            disabled={isLoading}
            options={data?.data.map(({ event_name }) => {
                return {
                    label: event_name,
                    value: event_name,
                };
            })}
            maxCount={mode === "single" ? 1 : undefined}
        />
    );
};

const FilterStringValueWithSuggestionInput = ({
    value,
    mode = "single",
    commonProps,
    assetMeta,
    onChange,
    productCode,
    platform,
    timeRange,
}: FilterStringValueInputProps) => {
    const apiUrl = useApiUrl("semantic");
    const currQueryFilters = [];

    if (timeRange !== undefined) {
        currQueryFilters.push({
            name: "date_tzutc",
            operator: OperatorEnum.IN_DATE_RANGE,
            values: timeRange?.map((t: any) => dayjs(t).format(config.DATE_FORMAT)),
        });
    }

    if (platform !== undefined && Array.isArray(platform) && platform.length > 0) {
        currQueryFilters.push({
            name: "ama_app_id",
            operator: OperatorEnum.IN,
            values: platform,
        });
    }

    const mergeJsonQuery = (q1: SmlJsonQuery, q2: SmlJsonQuery): SmlJsonQuery => {
        /*
    Only merge select & filters,
    Orders and limit are fallback from q1 to q2
    */
        return {
            select: Array.from(new Set([...(q1.select || []), ...(q2.select || [])])),
            filters: [...(q1.filters || []), ...(q2.filters || [])],
            orders: q1.orders || q2.orders,
            limit: q1.limit || q2.limit,
        };
    };

    const hasSuggestionGuideline = "common_values_query" in assetMeta.metadata;
    const shouldFetchData = hasSuggestionGuideline && productCode !== undefined && timeRange !== undefined;

    const commonValuesQuery = hasSuggestionGuideline
        ? mergeJsonQuery(assetMeta.metadata["common_values_query"], {
              select: [],
              filters: currQueryFilters,
              orders: [],
          })
        : {};

    const { data, isLoading, isError } = useCustom<QueryResultType>({
        url: `${apiUrl}/load`,
        method: "get",

        config: {
            headers: {
                "product-code": productCode,
            },
            query: {
                encoded_json_query: btoa(JSON.stringify(commonValuesQuery)),
            },
        },
        queryOptions: {
            retry: 2,
            enabled: shouldFetchData,
            cacheTime: 10 * 60 * 1000,
        },
    });

    const rendererMapping = createRendererMapping(data?.data?.meta?.schema || [], data?.data?.ref_assets || []);

    return (
        <Select
            {...commonProps}
            mode="tags"
            value={value}
            onChange={onChange}
            loading={isLoading}
            disabled={isLoading}
            options={
                isError
                    ? []
                    : data?.data.data.map((r) => {
                          const { [assetMeta.name]: value, ...attrs } = r;
                          return {
                              // label: r[assetMeta.name],
                              // value: r[assetMeta.name],

                              label: value,
                              value,
                              attrs,
                          };
                      })
            }
            optionRender={(option) => (
                <div className="m-1">
                    <div className="font-bold">{option.label}</div>
                    {Object.entries(option.data.attrs || {})
                        // .filter((e) => e[0] !== assetMeta.name)
                        .map(([assetName, value]) => {
                            return (
                                <div key={assetName} className="m-2">
                                    <span className="font-extralight">{assetName}: </span>
                                    {rendererMapping[assetName](value)}
                                </div>
                            );
                        })}
                </div>
            )}
            maxCount={mode === "single" ? 1 : undefined}
        />
    );
};

const FilterStringValueInput = ({
    value,
    mode = "single",
    commonProps,
    assetMeta,
    onChange,
    ...rest
}: FilterStringValueInputProps) => {
    const hasSuggestionGuideline = "common_values_query" in assetMeta.metadata;
    if (assetMeta.name === "event_name") {
        return (
            <FilterEventNameInput
                value={value}
                mode={mode}
                commonProps={commonProps}
                assetMeta={assetMeta}
                onChange={onChange}
                {...rest}
            />
        );
    } else if (hasSuggestionGuideline) {
        return (
            <FilterStringValueWithSuggestionInput
                value={value}
                mode={mode}
                commonProps={commonProps}
                assetMeta={assetMeta}
                onChange={onChange}
                {...rest}
            />
        );
    } else {
        return mode === "single" ? (
            <Input {...commonProps} value={value} onChange={(e) => onChange(e.target.value)} />
        ) : (
            <Select
                mode="tags"
                {...commonProps}
                value={value}
                onChange={onChange}
                placeholder="Enter multiple values"
            />
        );
    }
};

export const renderInputByDataTypeAndOperator = ({
    operator,
    dataType,
    value,
    onChange,
    assetMeta,
    ...rest
}: Props) => {
    const commonProps = {
        style: { width: "100%" },
    };

    const renderSingleInput = () => {
        switch (dataType) {
            case "string":
                return (
                    <FilterStringValueInput
                        key={assetMeta.id}
                        commonProps={commonProps}
                        value={value}
                        mode="multiple"
                        onChange={onChange}
                        assetMeta={assetMeta}
                        {...rest}
                    />
                );
            // return <Input {...commonProps} value={value} onChange={(e) => onChange(e.target.value)} />;
            case "int":
            case "float":
                return <InputNumber {...commonProps} value={value} onChange={onChange} />;
            case "date":
                return (
                    <DatePicker
                        {...commonProps}
                        value={value ? dayjs(value) : undefined}
                        onChange={(val) => onChange(dayjs(val).format(config.DATE_FORMAT) ?? null)}
                    />
                );
            case "bool":
                return (
                    <Select {...commonProps} value={value} onChange={onChange} placeholder="Select">
                        <Select.Option value={true}>True</Select.Option>
                        <Select.Option value={false}>False</Select.Option>
                    </Select>
                );
            default:
                return <Input {...commonProps} value={value} onChange={(e) => onChange(e.target.value)} />;
        }
    };

    const renderBetweenInput = () => {
        const val = Array.isArray(value) ? value : [null, null];
        const handleChange = (index: number, v: any) => {
            const newValue = [...val];
            newValue[index] = v;
            onChange(newValue);
        };

        switch (dataType) {
            case "date": {
                if (operator === OperatorEnum.BETWEEN) {
                    return (
                        <DatePicker.RangePicker
                            {...commonProps}
                            value={val && val[0] && val[1] ? [dayjs(val[0]), dayjs(val[1])] : undefined}
                            onChange={(dates) =>
                                onChange(
                                    dates
                                        ? [
                                              dayjs(dates[0])?.format(config.DATE_FORMAT),
                                              dayjs(dates[1])?.format(config.DATE_FORMAT),
                                          ]
                                        : [],
                                )
                            }
                        />
                    );
                } else {
                    return (
                        <Select
                            {...commonProps}
                            options={[7, 14, 30, 90]
                                .map((_v) => `Last ${_v} Days`)
                                .map((_v) => ({
                                    label: _v,
                                    value: _v,
                                }))}
                            value={value}
                            onChange={onChange}
                            placeholder="Enter multiple values"
                        />
                    );
                }
            }

            case "int":
            case "float":
                return (
                    <Space>
                        <InputNumber placeholder="From" value={val?.[0]} onChange={(v) => handleChange(0, v)} />
                        <InputNumber placeholder="To" value={val?.[1]} onChange={(v) => handleChange(1, v)} />
                    </Space>
                );
            default:
                return (
                    <Space>
                        <Input placeholder="From" value={val?.[0]} onChange={(e) => handleChange(0, e.target.value)} />
                        <Input placeholder="To" value={val?.[1]} onChange={(e) => handleChange(1, e.target.value)} />
                    </Space>
                );
        }
    };

    const renderInInput = () => {
        const val: any[] = Array.isArray(value) ? value : [];

        switch (dataType) {
            case "string":
                return (
                    <FilterStringValueInput
                        key={assetMeta.id}
                        commonProps={commonProps}
                        value={val}
                        mode="multiple"
                        onChange={onChange}
                        assetMeta={assetMeta}
                        {...rest}
                    />
                );
            default: {
                return (
                    <Select
                        mode="tags"
                        {...commonProps}
                        value={val}
                        onChange={onChange}
                        placeholder="Enter multiple values"
                    />
                );
            }
        }
    };

    if ([OperatorEnum.IN_DATE_RANGE, OperatorEnum.BETWEEN].includes(operator)) return renderBetweenInput();
    if (operator === OperatorEnum.IN) return renderInInput();
    if (operator === OperatorEnum.IS_NULL || operator === OperatorEnum.IS_NOT_NULL) return null;
    if (operator === OperatorEnum.LIKE)
        return <Input {...commonProps} value={value} onChange={(e) => onChange(e.target.value)} />;

    return renderSingleInput();
};
